'use client';

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Users, Plus, X, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PlayerSetup() {
  const [isOpen, setIsOpen] = useState(false);
  const [players, setPlayers] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [mounted, setMounted] = useState(false);

  // Button Ref für Positionierung
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);

  const fetchPlayers = async () => {
    const res = await fetch('/api/users');
    const data = await res.json();
    setPlayers(data);
  };

  useEffect(() => {
    fetchPlayers();
    setMounted(true);
  }, []);

  const toggleOpen = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
    setButtonRect(rect);
    setIsOpen(!isOpen);
  };

  const updatePlayer = async (action: 'add' | 'remove', name?: string) => {
    if (action === 'add' && !newName) return;
    
    await fetch('/api/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, name: name || newName })
    });
    
    setNewName('');
    fetchPlayers();
  };

  // Portal Content
  const dropdownContent = (
    <AnimatePresence>
      {isOpen && buttonRect && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
          
          {/* Dropdown positioned absolutely based on button */}
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            style={{
              position: 'fixed',
              top: buttonRect.bottom + 10,
              left: buttonRect.right - 320, // 320px width aligned to right
              width: '320px',
              zIndex: 9999
            }}
            className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-4"
          >
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-blue-500" /> Spieler verwalten
            </h3>

            {/* Input */}
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && updatePlayer('add')}
                placeholder="Name eingeben..."
                className="flex-1 bg-slate-950 border border-slate-700 rounded px-3 py-1 text-sm text-white focus:border-blue-500 outline-none"
                autoFocus
              />
              <button 
                onClick={() => updatePlayer('add')}
                className="bg-blue-600 hover:bg-blue-500 text-white p-2 rounded transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* List */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {players.length === 0 && (
                <p className="text-slate-500 text-xs italic text-center py-2">
                  Keine Spieler eingetragen.<br/>Roulette gilt für ALLE.
                </p>
              )}
              
              {players.map((player) => (
                <div key={player} className="flex items-center justify-between bg-slate-800/50 px-3 py-2 rounded border border-slate-800">
                  <span className="text-sm font-medium text-slate-200">{player}</span>
                  <button 
                    onClick={() => updatePlayer('remove', player)}
                    className="text-slate-500 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <button 
        onClick={toggleOpen}
        className={`border rounded-lg px-4 py-2 flex items-center gap-3 transition-all ${isOpen ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-900 border-slate-800 hover:border-blue-500'}`}
      >
        <Users className={isOpen ? "text-white" : "text-blue-500"} />
        <div className="text-left">
          <p className="text-[10px] uppercase opacity-70 leading-none">Spieler</p>
          <p className="text-sm font-bold">{players.length > 0 ? `${players.length} Aktiv` : 'Setup'}</p>
        </div>
      </button>

      {mounted && createPortal(dropdownContent, document.body)}
    </>
  );
}
